import * as React from "react";
import type { Column } from "../api.js";
import { Icon } from "./Icon.js";

export function ColumnManager({
  columns,
  visibleIds,
  defaultVisibleIds,
  onChange,
  columnOrder,
  onColumnOrderChange,
}: {
  columns: Column[];
  visibleIds: string[];
  defaultVisibleIds: string[];
  onChange: (ids: string[]) => void;
  columnOrder: string[];
  onColumnOrderChange: (ids: string[]) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [dropId, setDropId] = React.useState<string | null>(null);

  const matches = React.useCallback(
    (c: Column) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return c.label.toLowerCase().includes(q) || c.id.toLowerCase().includes(q);
    },
    [search],
  );

  const byId = React.useMemo(() => {
    const m = new Map<string, Column>();
    for (const c of columns) m.set(c.id, c);
    return m;
  }, [columns]);

  const visibleSet = React.useMemo(() => new Set(visibleIds), [visibleIds]);

  // Effective order is the saved column order, with any new columns (not yet
  // tracked) appended in their natural data order. Defends against drift
  // between data.columns and a saved view's columnOrder.
  const effectiveOrder = React.useMemo(() => {
    const seen = new Set(columnOrder);
    const tail = columns.map((c) => c.id).filter((id) => !seen.has(id));
    return columnOrder.filter((id) => byId.has(id)).concat(tail);
  }, [columnOrder, columns, byId]);

  const visibleColumns = React.useMemo(() => {
    const out: Column[] = [];
    for (const id of effectiveOrder) {
      const c = byId.get(id);
      if (c && visibleSet.has(id) && matches(c)) out.push(c);
    }
    return out;
  }, [effectiveOrder, byId, visibleSet, matches]);

  const hiddenGroups = React.useMemo(() => {
    const grouped = new Map<string, Column[]>();
    for (const id of effectiveOrder) {
      const c = byId.get(id);
      if (!c || visibleSet.has(id) || !matches(c)) continue;
      const list = grouped.get(c.group) ?? [];
      list.push(c);
      grouped.set(c.group, list);
    }
    return [...grouped.entries()];
  }, [effectiveOrder, byId, visibleSet, matches]);

  function toggle(id: string) {
    if (visibleSet.has(id)) {
      onChange(visibleIds.filter((x) => x !== id));
    } else {
      onChange([...visibleIds, id]);
    }
  }

  function showAll() {
    onChange(columns.map((c) => c.id));
  }

  function clearAll() {
    onChange([]);
  }

  function reset() {
    onChange(defaultVisibleIds);
  }

  function handleDragStart(e: React.DragEvent<HTMLDivElement>, id: string) {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>, id: string) {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropId !== id) setDropId(id);
  }

  function handleDragEnd() {
    setDragId(null);
    setDropId(null);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>, targetId: string) {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null);
    setDropId(null);
    if (!sourceId || sourceId === targetId) return;
    const next = effectiveOrder.filter((x) => x !== sourceId);
    const targetIdx = next.indexOf(targetId);
    if (targetIdx < 0) return;
    next.splice(targetIdx, 0, sourceId);
    onColumnOrderChange(next);
  }

  const dragEnabled = !search.trim() && visibleColumns.length > 1;
  const noResults = search.trim() && visibleColumns.length === 0 && hiddenGroups.length === 0;

  return (
    <div className="cm-inline">
      <div className="cm-search">
        <Icon name="search" size={13} />
        <input
          className="text"
          placeholder="Find a column…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ height: 32, paddingLeft: 30, fontSize: 13 }}
        />
      </div>
      <div className="cm-body">
        {noResults && (
          <div style={{ padding: 16, fontSize: 13, color: "var(--ink-500)", textAlign: "center" }}>
            No columns match "{search}".
          </div>
        )}
        {visibleColumns.length > 0 && (
          <div className="cm-group">
            <div className="cm-grouph">
              Visible
              <span className="cm-count">{visibleColumns.length}</span>
            </div>
            {visibleColumns.map((c) => {
              const isDragging = dragId === c.id;
              const isDropTarget = dropId === c.id && dragId !== c.id;
              return (
                <div
                  key={c.id}
                  className={
                    "cm-row visible"
                    + (isDragging ? " dragging" : "")
                    + (isDropTarget ? " drop-target" : "")
                  }
                  draggable={dragEnabled}
                  onDragStart={(e) => handleDragStart(e, c.id)}
                  onDragOver={(e) => handleDragOver(e, c.id)}
                  onDrop={(e) => handleDrop(e, c.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => toggle(c.id)}
                  role="button"
                >
                  <Icon
                    name="drag"
                    size={12}
                    className={"grip" + (dragEnabled ? "" : " grip-off")}
                  />
                  <span className="cm-label">{c.label}</span>
                  <button
                    className="vis-btn on"
                    onClick={(e) => { e.stopPropagation(); toggle(c.id); }}
                    title="Hide"
                    aria-label="Hide column"
                  >
                    <Icon name="eye" size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {hiddenGroups.map(([group, cols]) => (
          <div key={group} className="cm-group">
            <div className="cm-grouph">{group}</div>
            {cols.map((c) => (
              <div key={c.id} className="cm-row hidden" onClick={() => toggle(c.id)} role="button">
                <span className="cm-grip-spacer" />
                <span className="cm-label dim">{c.label}</span>
                <button
                  className="vis-btn"
                  onClick={(e) => { e.stopPropagation(); toggle(c.id); }}
                  title="Show"
                  aria-label="Show column"
                >
                  <Icon name="eyeOff" size={14} />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="cm-foot">
        <button className="btn btn-ghost btn-sm" onClick={clearAll}>Clear all</button>
        <button className="btn btn-ghost btn-sm" onClick={reset}>Reset</button>
        <button className="btn btn-ghost btn-sm" onClick={showAll}>Show all</button>
      </div>

      <style>{`
        .cm-inline { display: flex; flex-direction: column; }
        .cm-search { position: relative; padding: 4px 0 8px; }
        .cm-search > svg { position: absolute; left: 10px; top: 13px; color: var(--ink-400); }
        .cm-body { overflow: auto; max-height: 360px; padding: 0 0 4px; }
        .cm-group { padding: 4px 0; }
        .cm-grouph {
          display: flex; align-items: center; gap: 6px;
          font-size: 10.5px; font-weight: 800; letter-spacing: 0.08em;
          text-transform: uppercase; color: var(--ink-400);
          padding: 4px 8px;
        }
        .cm-count {
          background: var(--ink-100); color: var(--ink-600);
          border-radius: var(--r-full);
          padding: 1px 6px; font-size: 10px; font-weight: 800;
          letter-spacing: 0; text-transform: none;
          font-variant-numeric: tabular-nums;
        }
        .cm-row {
          display: flex; align-items: center; gap: 8px;
          padding: 6px 8px; border-radius: 6px; cursor: pointer;
          user-select: none;
          border: 1px solid transparent;
        }
        .cm-row:hover { background: var(--ink-100); }
        .cm-row.visible[draggable="true"] { cursor: grab; }
        .cm-row.visible[draggable="true"]:active { cursor: grabbing; }
        .cm-row.dragging { opacity: 0.4; }
        .cm-row.drop-target { border-top-color: var(--brand-500); }
        .cm-row .grip { color: var(--ink-300); flex-shrink: 0; }
        .cm-row .grip-off { color: var(--ink-200); }
        .cm-grip-spacer { width: 12px; flex-shrink: 0; }
        .cm-label { flex: 1; font-size: 13px; color: var(--ink-800); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cm-label.dim { color: var(--ink-500); }
        .vis-btn { width: 26px; height: 26px; border-radius: 6px; display: grid; place-content: center; color: var(--ink-300); border: 0; background: transparent; cursor: pointer; flex-shrink: 0; }
        .vis-btn.on { color: var(--brand-500); }
        .vis-btn:hover { background: var(--ink-200); }
        .cm-foot {
          display: flex; gap: 6px; align-items: center; flex-wrap: wrap;
          padding: 8px 0 4px;
          border-top: 1px solid var(--ink-100);
          margin-top: 6px;
        }
      `}</style>
    </div>
  );
}
